import { useNode } from '@craftjs/core';
import {
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import useResponsiveValues, {
  ResponsiveValue,
} from '@/components/editor/hooks/useResponsiveValues';

export enum ButtonColor {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  SUCCESS = 'success',
  ERROR = 'error',
  INFO = 'info',
  WARNING = 'warning',
}

export enum ButtonVariant {
  CONTAINED = 'contained',
  OUTLINED = 'outlined',
  TEXT = 'text',
}

export enum ButtonSize {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
}

export enum ButtonTarget {
  SELF = '_self',
  BLANK = '_blank',
}

export enum ButtonType {
  BUTTON = 'button',
  SUBMIT = 'submit',
  RESET = 'reset',
}

export interface ButtonProps {
  text?: string;
  size?: ButtonSize | ResponsiveValue<ButtonSize>;
  variant?: ButtonVariant | ResponsiveValue<ButtonVariant>;
  color?: ButtonColor | ResponsiveValue<ButtonColor>;
  fullWidth?: boolean | ResponsiveValue<boolean>;
  url?: string | ResponsiveValue<string>;
  target?: string | ResponsiveValue<ButtonTarget>;
  type?: ButtonType | ResponsiveValue<ButtonType>;
}

export interface Props extends ButtonProps {
  cssClass?: string | ResponsiveValue<string>;
}

const ClassSettings = () => {
  const {
    props,
    actions: { setProp },
  } = useNode((node) => ({
    props: node.data.props,
  }));

  const responsive = useResponsiveValues();

  console.log(responsive.getValue(props.variant));

  return (
    <Stack spacing={1}>
      <TextField
        label="Texto"
        name="text"
        value={responsive.getValue(props.text)}
        onChange={(e) => {
          setProp((props: Props) => {
            props.text = e.target.value;
            return props;
          });
        }}
        size="small"
      />
      <InputLabel>URL</InputLabel>
      <TextField
        name="url"
        value={responsive.getValue(props.url)}
        onChange={(e) => {
          setProp((props: Props) => {
            props.url = e.target.value;
            return props;
          });
        }}
        size="small"
        multiline
        minRows={2}
      />
      <InputLabel>Tamaño</InputLabel>
      <RadioGroup
        row
        color="primary"
        value={responsive.getValue(props.size) ?? ''}
        defaultValue={ButtonSize.MEDIUM}
        onChange={(_, value) => {
          setProp((props: Props) => {
            props.size = responsive.setValue(props.size, value);
            return props;
          });
        }}
      >
        <FormControlLabel
          control={<Radio />}
          value={ButtonSize.SMALL}
          label="Pequeño"
        />
        <FormControlLabel
          control={<Radio />}
          value={ButtonSize.MEDIUM}
          label="Mediano"
        />
        <FormControlLabel
          control={<Radio />}
          value={ButtonSize.LARGE}
          label="Grande"
        />
      </RadioGroup>
      <InputLabel>Variante</InputLabel>
      <RadioGroup
        row
        color="primary"
        value={responsive.getValue(props.variant) ?? ''}
        defaultValue={ButtonVariant.CONTAINED}
        onChange={(_, value) => {
          setProp((props: Props) => {
            props.variant = responsive.setValue(props.variant, value);
            return props;
          });
        }}
      >
        <FormControlLabel
          control={<Radio />}
          value={ButtonVariant.CONTAINED}
          label="Relleno"
        />
        <FormControlLabel
          control={<Radio />}
          value={ButtonVariant.OUTLINED}
          label="Contorno"
        />
        <FormControlLabel
          control={<Radio />}
          value={ButtonVariant.TEXT}
          label="Texto"
        />
      </RadioGroup>
      <InputLabel>Color</InputLabel>
      <RadioGroup
        row
        color="primary"
        value={responsive.getValue(props.color) ?? ''}
        defaultValue={ButtonColor.PRIMARY}
        onChange={(_, value) => {
          setProp((props: Props) => {
            props.color = responsive.setValue(props.color, value);
            return props;
          });
        }}
      >
        <FormControlLabel
          control={<Radio />}
          value={ButtonColor.PRIMARY}
          label="Primario"
        />
        <FormControlLabel
          control={<Radio />}
          value={ButtonColor.SECONDARY}
          label="Secundario"
        />
        <FormControlLabel
          control={<Radio />}
          value={ButtonColor.SUCCESS}
          label="Éxito"
        />
        <FormControlLabel
          control={<Radio />}
          value={ButtonColor.ERROR}
          label="Error"
        />
        <FormControlLabel
          control={<Radio />}
          value={ButtonColor.INFO}
          label="Información"
        />
        <FormControlLabel
          control={<Radio />}
          value={ButtonColor.WARNING}
          label="Advertencia"
        />
        <FormControlLabel control={<Radio />} value="white" label="Blanco" />
      </RadioGroup>

      <InputLabel>Destino</InputLabel>
      <RadioGroup
        row
        color="primary"
        value={responsive.getValue(props.target) ?? ''}
        defaultValue={ButtonTarget.SELF}
        onChange={(_, value) => {
          setProp((props: Props) => {
            props.target = responsive.setValue(props.target, value);
            return props;
          });
        }}
      >
        <FormControlLabel
          control={<Radio />}
          value={ButtonTarget.SELF}
          label="Interno"
        />
        <FormControlLabel
          control={<Radio />}
          value={ButtonTarget.BLANK}
          label="Externo"
        />
      </RadioGroup>

      <InputLabel>Type</InputLabel>
      <RadioGroup
        row
        color="primary"
        value={responsive.getValue(props.type) ?? ''}
        defaultValue={ButtonType.BUTTON}
        onChange={(_, value) => {
          setProp((props: Props) => {
            props.type = responsive.setValue(props.type, value as ButtonType);
            return props;
          });
        }}
      >
        <FormControlLabel
          control={<Radio />}
          value={ButtonType.BUTTON}
          label="Button"
        />
        <FormControlLabel
          control={<Radio />}
          value={ButtonType.SUBMIT}
          label="Submit"
        />
        <FormControlLabel
          control={<Radio />}
          value={ButtonType.RESET}
          label="Reset"
        />
      </RadioGroup>

      <FormControl component="fieldset">
        <FormGroup aria-label="position" row>
          <FormControlLabel
            value={responsive.getValue(props.variant) ?? false}
            control={<Switch color="primary" />}
            label="Fluido"
            labelPlacement="start"
            onChange={(_, value) => {
              setProp((props: Props) => {
                props.fullWidth = responsive.setValue(props.fullWidth, value);
                return props;
              });
            }}
          />
        </FormGroup>
      </FormControl>
    </Stack>
  );
};

export default ClassSettings;
